import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Environment from '@config/index';
import Icon from '@components/atoms/icon/icon';
import { useAnalytics } from '@context/analytics.context';
import useWelcomeVoucherQuery from '@hooks/user/use-welcome-voucher-query';
import usePageType from '@hooks/use-page-type';
import { PAGETYPE_TO_TOUCHPOINT } from '@constants/welcome-voucher';
import { useBucket } from '@context/bucket.context';
import Cookies from 'universal-cookie';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import styles from './smart-banner.module.scss';

type BannerVariantType =
  | 'control'
  | 'better_experience'
  | 'commercial_precise'
  | 'commercial_precise_fallback'
  | 'commercial_vague:'
  | 'stars_only'
  | '';

type BannerTextsType = {
  title?: string;
  content?: string;
  cta: string;
  link: string;
};

type BannerTextsListType = {
  [name: string]: BannerTextsType;
};

interface SmartBannerProps {
  onCloseBannerClick: () => void;
  showBanner: boolean;
  osList?: {
    isIos: boolean;
    isAndroid: boolean;
  };
}

const SmartBanner: React.FC<SmartBannerProps> = (props): React.JSX.Element => {
  const { onCloseBannerClick, showBanner, osList } = props;
  const { t } = useTranslation('default-layout');

  const { getFeature } = useBucket();
  const { scriptsEventData } = useAnalytics();
  const { sendAnalyticEvent } = useAnalyticEvents('smart_banner');
  const { pageType } = usePageType();
  const { welcomeVoucher } = useWelcomeVoucherQuery({ touchPoint: PAGETYPE_TO_TOUCHPOINT[pageType] });
  const cookies: Cookies = new Cookies();
  const adjCampaign = cookies.get('adjCampaign');
  const bannerVariation: BannerVariantType = getFeature('appBannerVariations', true)?.value;

  // Variables for Banner variations A/B/C/D test
  const bannerVariant = useMemo(() => {
    if (!showBanner) {
      return '';
    }
    if (!welcomeVoucher || (bannerVariation === 'commercial_precise' && welcomeVoucher?.discount?.formatted === '0')) {
      return 'commercial_precise_fallback';
    }
    return bannerVariation;
  }, [showBanner, welcomeVoucher, bannerVariation]);

  const getAppUrl = (adjustLink: string): string => {
    if (typeof window === 'undefined') {
      return '';
    }
    return `${adjustLink}&adj_deep_link=com.vestiairecollective.vestiaire%3A%2F%2Faction%3Ftype%3Dwebbanner%26url%3D${encodeURIComponent(
      window.location.href,
    )}${adjCampaign ? '&adj_campaign=' + adjCampaign : ''}`;
  };

  const bannerTexts: BannerTextsListType = {
    control: {
      title: t('SMART_APP_BANNER.APP_NAME'),
      content: t('SMART_APP_BANNER.CONTEXT_INFO_IOS'),
      cta: t('SMART_APP_BANNER.OPEN_BUTTON'),
      link: getAppUrl('https://wj62.adj.st/action?type=start&adj_t=pjs36go_5kli5il'),
    },
    better_experience: {
      title: null,
      content: t('SMART_APP_BANNER.SMALL.CONTENT.EXPERIENCE'),
      cta: t('SMART_APP_BANNER.SMALL.CTA'),
      link: getAppUrl('https://wj62.adj.st/action?type=start&adj_t=12v7j4ie_12jifi3e'),
    },
    commercial_precise: {
      title: null,
      content: t('SMART_APP_BANNER.SMALL.CONTENT', { promoOffer: welcomeVoucher?.discount?.formatted }),
      cta: t('SMART_APP_BANNER.SMALL.CTA'),
      link: getAppUrl('https://wj62.adj.st/action?type=start&adj_t=126tzp0b_12rumddw'),
    },
    commercial_precise_fallback: {
      title: null,
      content: t('SMART_APP_BANNER.CONTEXT_INFO_IOS'),
      cta: t('SMART_APP_BANNER.SMALL.CTA'),
      link: getAppUrl('https://wj62.adj.st/action?type=start&adj_t=126tzp0b_12rumddw'),
    },
    commercial_vague: {
      title: null,
      content: t('SMART_APP_BANNER.CONTEXT_INFO_IOS'),
      cta: t('SMART_APP_BANNER.SMALL.CTA'),
      link: getAppUrl('https://wj62.adj.st/action?type=start&adj_t=136p2qq5_13zilkwe'),
    },
    stars_only: {
      title: t('SMART_APP_BANNER.VARIATION.TITLE'),
      content: null,
      cta: t('SMART_APP_BANNER.SMALL.CTA'),
      link: getAppUrl('https://wj62.adj.st/action?type=start&adj_t=12grmoub_12s3r2ym'),
    },
  };

  const closeBanner = (): void => {
    sendAnalyticEvent('banner_app_download_close', { property: bannerVariant });
    onCloseBannerClick();
  };

  const bannerClickOpen = (): void => {
    sendAnalyticEvent('banner_app_download_open', { property: bannerVariant });
  };

  useEffect(() => {
    if (showBanner && scriptsEventData?.isTcEvents1Loaded && scriptsEventData?.isTcEvents7Loaded) {
      sendAnalyticEvent('banner_app_download_view', {
        property: bannerVariant,
      });
    }
  }, [scriptsEventData?.isTcEvents1Loaded, scriptsEventData?.isTcEvents7Loaded]);

  if (!bannerVariation) {
    return null;
  }

  return (
    <>
      <div className={styles.smartBanner}>
        <button
          data-cy="smart-banner-close-button"
          className={styles.smartBanner__closeBtn}
          type="button"
          onClick={closeBanner}
          aria-label="close"
        >
          <Icon name="close-default" />
          <span className="visuallyhidden">{t('MODAL.BUTTON.CLOSE')}</span>
        </button>
        <figure className={styles.smartBanner__figure}>
          <img
            className={styles.smartBanner__figure__logo}
            src={`${Environment.assetsPath}/images/icons/logo.svg`}
            alt={t('SEO.LOGO_ALT')}
          />
        </figure>
        <div
          className={clsx(
            styles.smartBanner__info,
            bannerVariant === 'control' && styles['smartBanner__info--control'],
          )}
        >
          <p className={styles.smartBanner__info__title}>{bannerTexts[bannerVariant]?.title}</p>
          <p className={styles.smartBanner__info__contextInfo}>
            <span>{bannerTexts[bannerVariant]?.content}</span>
          </p>
          <p className={styles.smartBanner__info__note}>
            {[0, 1, 2, 3, 4].map((n: number) => {
              return (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  key={n}
                  className={clsx(
                    styles.smartBanner__info__note__svgStar,
                    n === 4 && styles['smartBanner__info__note__svgStar--last'],
                  )}
                >
                  <path
                    d="M9.92855 10.2504L11.5085 2.15568H12.49L14.07 10.2504L21.6726 9.06989L22.0277 9.97928L15.6457 14.2577L18.9545 21.5447L18.1648 22.1231L11.9993 16.5741L5.83376 22.1231L5.04401 21.5447L8.35283 14.2577L1.97086 9.97928L2.32599 9.06989L9.92855 10.2504Z"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    fill="currentColor"
                  />
                  {n === 4 && (
                    <path
                      d="M9.92857 10.2504L11.5093 2.15419L12 2.15V16.58L5.83644 22.1207L5.04401 21.5447L8.35283 14.2577L1.97086 9.97928L2.32599 9.06989L9.92857 10.2504Z"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      fill="currentColor"
                      className={styles.smartBanner__info__note__svgStar__half}
                    />
                  )}
                </svg>
              );
            })}
            <span className={styles.smartBanner__info__note__numbers}>
              {osList?.isIos && '(45k)'}
              {osList?.isAndroid && '(22k)'}
            </span>
          </p>
        </div>
        <div className={styles.smartBanner__action}>
          <a
            href={bannerTexts[bannerVariant]?.link}
            className={styles.smartBanner__action__openBtn}
            onClick={bannerClickOpen}
          >
            <span>{bannerTexts[bannerVariant]?.cta}</span>
          </a>
        </div>
      </div>
    </>
  );
};

export default SmartBanner;
